// src\modules\Products\List\components\modals\AdjustStockModal.tsx
import { useToast } from '@shared/hooks/use-toast';
import { createHttpClient } from '@adapters/HttpClient';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { StepperInput } from '@shared/components/ui/StepperInput';
import { faArrowRight, faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { apiClientWH } from '@shared/services';
import { useQueryClient } from '@tanstack/react-query';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductsApiAdapter } from '../../../CreateEdit/adapters/ProductsApiAdapter';
import { BatchSelect } from '@modules/Batches/components/BatchSelect';

export interface AdjustStockModalRef {
  /**
   * Abre la modal de ajuste para una ubicación específica
   */
  open: (
    options: {
      productId: string | number;
      locationId: string | number;
      locationCode: string;
      currentQty?: number;
    },
    onConfirm?: (data: { unidades: number; descripcion: string }) => void,
  ) => void;
}

export const AdjustStockModal = forwardRef<AdjustStockModalRef>((_, ref) => {
  const { t } = useTranslation('productsModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [productId, setProductId] = useState<string | number | null>(null);
  const [locationId, setLocationId] = useState<string | number | null>(null);
  const [locationCode, setLocationCode] = useState<string>('');
  const [currentQty, setCurrentQty] = useState<number>(0);
  const [unidades, setUnidades] = useState<number>(0);
  const [descripcion, setDescripcion] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const confirmRef = useRef<(data: { unidades: number; descripcion: string }) => void>(() => {});

  useImperativeHandle(ref, () => ({
    open: (opts, onConfirm) => {
      setProductId(opts.productId);
      setLocationId(opts.locationId);
      setLocationCode(opts.locationCode);
      setCurrentQty(opts.currentQty ?? 0);
      setUnidades(0);
      setDescripcion('');
      setSelectedBatchId(null);
      confirmRef.current = onConfirm || (() => {});
      modalRef.current?.openModal();
    },
  }));

  const handleConfirm = async () => {
    if (!productId || !locationId || unidades === 0) {
      toast({
        title: t('toasts.stock_adjust_error'),
        description: t('modal.adjust_stock_validation_error'),
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new ProductsApiAdapter(httpClient);
      await adapter.adjustStock({
        productId,
        locationId,
        quantity: unidades,
        description: descripcion,
        batchId: selectedBatchId ?? undefined,
      });

      toast({
        title: t('toasts.stock_adjust_success'),
        description: t('modal.adjust_stock_note'),
        variant: 'success',
      });

      await queryClient.invalidateQueries({
        queryKey: ['locations-by-product', productId],
      });

      confirmRef.current({ unidades, descripcion });
      modalRef.current?.closeModal();
    } catch (error: any) {
      const data = error?.response?.data;
      const apiMessage =
        data?.message ||
        data?.error?.message ||
        (Array.isArray(data?.errors) && data.errors[0]?.message) ||
        data?.error_description ||
        error?.message;
      toast({
        title: t('toasts.stock_adjust_error'),
        description: apiMessage || t('toasts.error_title'),
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const newStock = currentQty + unidades;

  return (
    <ModalCenter
      ref={modalRef}
      title={
        <span className="flex items-center gap-2">
          {t('modal.adjust_stock_title')}
          {locationCode && (
            <Badge variant="default" className="ml-2 font-normal text-xs text-white">
              <FontAwesomeIcon icon={faLocationDot} className="w-3 h-3 mr-1" />
              {locationCode}
            </Badge>
          )}
        </span>
      }
      type="default"
      width="lg"
      fullScreenMobile={true}
    >
      <div className="space-y-6 pt-4 text-title">
        {/* Texto informativo */}
        <div className="text-sm">
          <p className="mb-2">{t('modal.adjust_stock_note')}</p>
        </div>

        {/* Stock visual con flecha */}
        <div className="flex items-center justify-center gap-8 py-4 !mt-4">
          <div className="text-center">
            <div className="text-xl font-medium pb-2">{currentQty}</div>
            <div className="text-sm font-normal">{t('modal.current_stock_label')}</div>
          </div>

          <FontAwesomeIcon icon={faArrowRight} className="w-5 h-5" />

          <div className="text-center">
            <div
              className={`text-xl font-medium pb-2 ${
                unidades === 0 ? '' : unidades > 0 ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {newStock}
            </div>
            <div className="text-sm font-normal">{t('modal.new_stock_label')}</div>
          </div>
        </div>

        {/* Batch selection */}
        {productId && (
          <BatchSelect productId={Number(productId)} value={selectedBatchId} onChange={setSelectedBatchId} />
        )}

        {/* Formulario */}
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm mb-2 text-text">{t('modal.adjustment_label')}</label>
              <StepperInput
                value={unidades}
                onChange={setUnidades}
                min={-currentQty}
                max={999999}
                data-testid="adjust-stock-units-stepper"
              />
              {unidades < 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {t('modal.max_to_remove')}: {currentQty} {t('modal.units')}
                </p>
              )}
            </div>

            <div className="col-span-2">
              <label className="block text-sm mb-2 text-text">{t('modal.description_label')}</label>
              <Input
                placeholder={t('modal.description_placeholder')}
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                data-testid="adjust-stock-description-input"
              />
            </div>
          </div>
        </div>

        {/* Botonera */}
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            size="lg"
            onClick={() => modalRef.current?.closeModal()}
            data-testid="adjust-stock-cancel-button"
          >
            {t('modal.cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleConfirm}
            disabled={unidades === 0 || submitting || !productId || !locationId}
            data-testid="adjust-stock-confirm-button"
          >
            {t('modal.adjust')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

AdjustStockModal.displayName = 'AdjustStockModal';
